import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Container, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import loadService from '../../services/loadService';
import userService from '../../services/userService';
import { SearchAndFilterBar } from '../../components/search-and-filter-bar';
import { getLoadHistoryFilterConfig } from '../../components/modals/universal-filter/filterConfigs';
import Loading from '../../components/loading-animation';
import EmptyState from '../../components/not-result';
import Pagination from '../../components/pagination/Pagination';
import './LoadHistoryListPage.scss';

const ITEMS_PER_PAGE = 10;
const ACTION_OPTIONS = [
  { value: '', label: 'All actions' },
  { value: 'created', label: 'Created' },
  { value: 'updated', label: 'Updated' },
  { value: 'status_update', label: 'Status changed' },
  { value: 'assign', label: 'Assigned' },
  { value: 'delete', label: 'Deleted' },
];

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const d = new Date(dateString);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getActionLabel = (action) => {
  const opt = ACTION_OPTIONS.find((o) => o.value === action);
  return opt ? opt.label : (action || '—').replace('_', ' ');
};

const getActorName = (actor) => {
  if (!actor) return '—';
  if (actor.actorId && typeof actor.actorId === 'object') {
    const u = actor.actorId;
    const name = [u.firstName, u.lastName].filter(Boolean).join(' ');
    return name || u.email || u.companyName || '—';
  }
  return actor.actorEmail || '—';
};

const getLoadDisplay = (load) => {
  if (!load) return '—';
  if (typeof load === 'object') {
    return load.orderId || load.billOfLadingNumber || '—';
  }
  return '—';
};

const getChangesSummary = (changes = []) => {
  if (!changes.length) return '—';
  if (changes.length === 1) return changes[0].field || '1 field';
  return `${changes.length} fields`;
};

const LoadHistoryListPage = () => {
  const { getThemeColors } = useTheme();
  const colors = getThemeColors();
  const navigate = useNavigate();

  const [list, setList] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: ITEMS_PER_PAGE,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedOrderId, setDebouncedOrderId] = useState('');
  const searchDebounceRef = useRef(null);
  const [appliedFilters, setAppliedFilters] = useState({
    action: '',
    userId: '',
    dateFrom: '',
    dateTo: '',
  });
  const loadHistoryFilterConfig = useMemo(() => getLoadHistoryFilterConfig(users), [users]);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedOrderId(searchTerm.trim());
    }, 400);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchTerm]);

  useEffect(() => {
    if (debouncedOrderId) {
      setPagination((prev) => (prev.currentPage === 1 ? prev : { ...prev, currentPage: 1 }));
    }
  }, [debouncedOrderId]);

  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const res = await userService.getUsers({ limit: 500 });
      const data = res?.data?.data ?? res?.data ?? res;
      const arr = Array.isArray(data) ? data : data?.users ?? data?.list ?? [];
      setUsers(Array.isArray(arr) ? arr : []);
    } catch (e) {
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: pagination.currentPage,
        limit: pagination.limit,
        ...(debouncedOrderId && { orderId: debouncedOrderId }),
        ...(appliedFilters.action && { action: appliedFilters.action }),
        ...(appliedFilters.userId && { userId: appliedFilters.userId }),
        ...(appliedFilters.dateFrom && { dateFrom: appliedFilters.dateFrom }),
        ...(appliedFilters.dateTo && { dateTo: appliedFilters.dateTo }),
      };
      const res = await loadService.getLoadHistoryList(params);
      const api = res?.data ?? res;
      if (api?.success && Array.isArray(api?.data)) {
        setList(api.data);
        if (api.pagination) {
          setPagination((prev) => ({
            ...prev,
            total: api.pagination.total ?? prev.total,
            totalPages: api.pagination.totalPages ?? prev.totalPages,
            currentPage: api.pagination.currentPage ?? prev.currentPage,
            limit: api.pagination.limit ?? prev.limit,
          }));
        }
      } else {
        setList([]);
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load history');
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.limit, debouncedOrderId, appliedFilters]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleFilterApply = (filtersFromModal) => {
    setAppliedFilters({
      action: filtersFromModal.action ?? '',
      userId: filtersFromModal.userId ?? '',
      dateFrom: filtersFromModal.dateFrom ?? '',
      dateTo: filtersFromModal.dateTo ?? '',
    });
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  return (
    <Container fluid style={{ backgroundColor: colors.background, minHeight: '100vh' }} className="load-history-list-page">
      <div className="load-history-list-page__header">
        <h1 className="load-history-list-page__title">Load History</h1>
        <p className="load-history-list-page__subtitle">Who updated loads and when</p>
      </div>

      <div className="load-history-list-page__bar">
        <SearchAndFilterBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onFilterApply={handleFilterApply}
          filterConfig={loadHistoryFilterConfig}
          searchPlaceholder="Search by load (Order ID)..."
          pageType="loadHistory"
          disableAddModal
          showAddButton={false}
          initialFilters={appliedFilters}
        />
      </div>

      <div className="load-history-list-page__table-wrap">
        {loading ? (
          <Loading size="medium" />
        ) : error ? (
          <EmptyState variant="error" title="Error" message={error} onRetry={fetchHistory} retryText="Retry" />
        ) : list.length === 0 ? (
          <EmptyState variant="empty" title="No history" message="No load history records match your filters." />
        ) : (
          <>
            <Table responsive className="load-history-list-page__table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Load</th>
                  <th>Action</th>
                  <th>Who</th>
                  <th>What changed</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {list.map((item) => (
                  <tr key={item._id}>
                    <td className="load-history-list-page__cell-date">{formatDate(item.createdAt)}</td>
                    <td>
                      <button
                        type="button"
                        className="load-history-list-page__link"
                        onClick={() => navigate(`/loads/edit/${item.load?._id || item.load}`)}
                      >
                        {getLoadDisplay(item.load)}
                      </button>
                      <button
                        type="button"
                        className="load-history-list-page__link load-history-list-page__link--small"
                        onClick={() => navigate(`/loads/history/${item.load?._id || item.load}`)}
                      >
                        View timeline
                      </button>
                    </td>
                    <td>{getActionLabel(item.action)}</td>
                    <td>{getActorName(item.actor)}</td>
                    <td>{getChangesSummary(item.changes)}</td>
                    <td />
                  </tr>
                ))}
              </tbody>
            </Table>
            {pagination.totalPages > 1 && (
              <div className="load-history-list-page__pagination">
                <Pagination
                  currentPage={pagination.currentPage}
                  totalItems={pagination.total}
                  itemsPerPage={pagination.limit}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </div>
    </Container>
  );
};

export default LoadHistoryListPage;
